import React, { useEffect, useState, useMemo, useRef } from "react";
import { animate } from "animejs";
import { useDrawLoop } from "./utils";
import "./styles.css";

export const handler = ({ inputs, mechanic }) => {
  const {
    format,
    width,
    height,
    title,
    event1,
    event2,
    event3,
    event4,
    event5,
    event6,
    titleAlign,
    textAlign,
    gap,
    staggerDelay,
    animationDuration,
    duration
  } = inputs;

  // Calculate dimensions based on format
  const formatDimensions = {
    vertical: { width: 1080, height: 1350 },
    square: { width: 1080, height: 1080 },
    landscape: { width: 1080, height: 566 },
    story: { width: 1080, height: 1920 },
    custom: { width: width, height: height }
  };
  
  const dimensions = formatDimensions[format];
  const finalWidth = dimensions.width;
  const finalHeight = dimensions.height;

  const [fetchedEvents, setFetchedEvents] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  
  // Animation timing
  const startTime = useRef(Date.now());
  const isPlaying = useRef(true);
  const frameCount = useDrawLoop(isPlaying.current);
  
  // Refs for animation targets
  const titleRef = useRef(null);
  const textRef = useRef(null);
  
  // Store rotation values in refs so they don't change on re-render
  const titleRotationRef = useRef(null);
  const textRotationRef = useRef(null);
  
  // Initialize rotation values once
  if (titleRotationRef.current === null) {
    titleRotationRef.current = parseFloat((Math.random() * 20 - 10).toFixed(1));
  }
  if (textRotationRef.current === null) {
    textRotationRef.current = parseFloat((Math.random() * 20 - 10).toFixed(1));
  }

  // Fetch events from API once on initial load
  useEffect(() => {
    let isMounted = true;
    
    if (!hasFetched) {
      setHasFetched(true);
      
      fetch('https://cms.hetzner.gaswerkareal.sh/api/events')
        .then(res => res.json())
        .then(data => {
          if (isMounted) {
            setFetchedEvents(data);
          }
        })
        .catch(err => {
          console.error('Failed to fetch events:', err);
          if (isMounted) {
            setFetchedEvents([]);
          }
        });
    }
    
    return () => {
      isMounted = false;
    };
  }, [hasFetched]);

  // Use useMemo to cache formatted events
  const formattedFetchedEvents = useMemo(() => {
    return fetchedEvents.map(event => {
      const date = new Date(event.date);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const timeStr = event.time ? `${event.time}: ` : '';
      return `${month}.${day}, ${timeStr}${event.title}`;
    });
  }, [fetchedEvents]);

  // Merge manual and fetched events
  let events = [...formattedFetchedEvents];
  if (event1 && event1.trim()) events[0] = event1;
  if (event2 && event2.trim()) events[1] = event2;
  if (event3 && event3.trim()) events[2] = event3;
  if (event4 && event4.trim()) events[3] = event4;
  if (event5 && event5.trim()) events[4] = event5;
  if (event6 && event6.trim()) events[5] = event6;
  events = events.slice(0, 6).filter(e => e && e.trim());
  
  const displayText = events.map(e => `– ${e}`).join('\n');

  const getAlignSelf = (align) => {
    if (align === "left") return "flex-start";
    if (align === "right") return "flex-end";
    return "center";
  };

  // Setup anime.js animations once
  useEffect(() => {
    // Wait for elements to be in the DOM
    if (!titleRef.current || !textRef.current) {
      return;
    }

    // Capture rotation values from refs
    const titleRotation = titleRotationRef.current;
    const textRotation = textRotationRef.current;

    // Set initial state (off-screen to the left)
    titleRef.current.style.transform = `translateX(-150%) rotate(${titleRotation}deg)`;
    titleRef.current.style.opacity = '0';
    textRef.current.style.transform = `translateX(-150%) rotate(${textRotation}deg)`;
    textRef.current.style.opacity = '0';

    // Capture refs in local variables for callbacks
    const titleElement = titleRef.current;
    const textElement = textRef.current;

    // Create animations with stagger
    const titleAnimation = animate(titleElement, {
      translateX: { to: 0 },
      opacity: { to: 1 },
      duration: animationDuration,
      ease: 'outQuad',
      onUpdate: () => {
        if (!titleElement) return;
        // Apply rotation separately to maintain it
        const currentTransform = titleElement.style.transform;
        if (!currentTransform.includes('rotate')) {
          titleElement.style.transform = `${currentTransform} rotate(${titleRotation}deg)`;
        }
      }
    });

    // Text animation starts after stagger delay
    setTimeout(() => {
      if (!textElement) return;
      animate(textElement, {
        translateX: { to: 0 },
        opacity: { to: 1 },
        duration: animationDuration,
        ease: 'outQuad',
        onUpdate: () => {
          if (!textElement) return;
          const currentTransform = textElement.style.transform;
          if (!currentTransform.includes('rotate')) {
            textElement.style.transform = `${currentTransform} rotate(${textRotation}deg)`;
          }
        }
      });
    }, staggerDelay);
  }, [staggerDelay, animationDuration]);

  // Handle frame recording and animation completion
  useEffect(() => {
    if (Date.now() - startTime.current < duration * 1000) {
      mechanic.frame();
    } else if (isPlaying.current) {
      isPlaying.current = false;
      mechanic.done();
    }
  }, [frameCount, duration]);

  return (
    <div
      className="event-layout-container"
      style={{
        width: `${finalWidth}px`,
        height: `${finalHeight}px`,
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: `${gap}px`,
        padding: "60px 40px",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden"
      }}
    >
      <h1
        ref={titleRef}
        className="rotated-title"
        style={{
          color: "#6691cb",
          fontSize: "46px",
          fontWeight: 500,
          fontFamily: "Figtree, sans-serif",
          margin: 0,
          textAlign: titleAlign,
          textTransform: "lowercase",
          opacity: 0,
          transform: `translateX(-150%) rotate(${titleRotationRef.current}deg)`
        }}
      >
        {title}
      </h1>
      
      <p
        ref={textRef}
        className="rotated-text"
        style={{
          color: "#6691cb",
          fontSize: "26px",
          fontWeight: 400,
          fontFamily: "Figtree, sans-serif",
          margin: 0,
          textAlign: "left",
          maxWidth: "80%",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          alignSelf: getAlignSelf(textAlign),
          opacity: 0,
          transform: `translateX(-150%) rotate(${textRotationRef.current}deg)`
        }}
      >
        {displayText}
      </p>
    </div>
  );
};

export const inputs = {
  format: {
    type: "text",
    default: "vertical",
    options: ["vertical", "square", "landscape", "story", "custom"],
    label: "Format"
  },
  width: {
    type: "number",
    default: 1080,
    label: "Width",
    editable: (inputs) => inputs.format === "custom"
  },
  height: {
    type: "number",
    default: 1350,
    label: "Height",
    editable: (inputs) => inputs.format === "custom"
  },
  title: {
    type: "text",
    default: "Lorem ipsum dolor sit amet consectetur adipiscing"
  },
  titleAlign: {
    type: "text",
    default: "center",
    options: ["left", "center", "right"],
    label: "Title Alignment"
  },
  event1: {
    type: "text",
    default: "",
    label: "Event 1 (override)"
  },
  event2: {
    type: "text",
    default: "",
    label: "Event 2 (override)"
  },
  event3: {
    type: "text",
    default: "",
    label: "Event 3 (override)"
  },
  event4: {
    type: "text",
    default: "",
    label: "Event 4 (override)"
  },
  event5: {
    type: "text",
    default: "",
    label: "Event 5 (override)"
  },
  event6: {
    type: "text",
    default: "",
    label: "Event 6 (override)"
  },
  textAlign: {
    type: "text",
    default: "center",
    options: ["left", "center", "right"],
    label: "Text Position"
  },
  gap: {
    type: "number",
    default: 650,
    min: 0,
    max: 1100,
    step: 10,
    slider: true,
    label: "Vertical Gap (px)"
  },
  staggerDelay: {
    type: "number",
    default: 300,
    min: 0,
    max: 2000,
    step: 50,
    slider: true,
    label: "Stagger Delay (ms)"
  },
  animationDuration: {
    type: "number",
    default: 800,
    min: 200,
    max: 3000,
    step: 100,
    slider: true,
    label: "Animation Duration (ms)"
  },
  duration: {
    type: "number",
    default: 3,
    min: 1,
    max: 10,
    step: 0.5,
    slider: true,
    label: "Total Duration (seconds)"
  }
};

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "Event Layout Animated",
  animated: true,
  persistRandomOnExport: true,
  hidePresets: true,
  hideAutoRefresh: false,
  hideFunction: true
};
